export const UTM_COOKIE_KEYS = {
  UTM_SOURCE: "ib_utm_source",
  UTM_MEDIUM: "ib_utm_medium",
  UTM_CAMPAIGN: "ib_utm_campaign",
  ADMITAD_UID: "ib_admitad_uid",
  GCLID: "gclid",
  REFERRAL: "ib_referral",
  UTM_DATE_TIME: "ib_utm_date_time",
  DIRECT_DATE_TIME: "ib_direct_date_time",
  ACTIVITY_TIME: "ib_activity_time",
  TRANSACTION_TIME: "ib_transaction_time",
  LOGIN_ATTEMPT: "login_attempt",
  LEGACY: "legacy",
} as const;

export const UTM_COOKIE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const UTM_EXPIRY_WINDOW_MS = UTM_COOKIE_MAX_AGE_MS;

const UTM_COOKIE_CLEAR_SET: readonly string[] = [
  UTM_COOKIE_KEYS.UTM_CAMPAIGN,
  UTM_COOKIE_KEYS.UTM_MEDIUM,
  UTM_COOKIE_KEYS.UTM_SOURCE,
  UTM_COOKIE_KEYS.UTM_DATE_TIME,
  UTM_COOKIE_KEYS.TRANSACTION_TIME,
  UTM_COOKIE_KEYS.REFERRAL,
  UTM_COOKIE_KEYS.GCLID,
];

const BOT_KEYWORDS: readonly string[] = ["lighthouse", "bot", "google", "k6", "frog", "rush"];

export function isBotUserAgent(userAgent: string | undefined | null): boolean {
  const ua = (userAgent ?? "").toLowerCase();
  return BOT_KEYWORDS.some((keyword) => ua.includes(keyword));
}

export function toExpressCookieValue(value: string | Date): string {
  return typeof value === "string" ? value : `j:${JSON.stringify(value)}`;
}

export function fromExpressCookieValue(raw: string | undefined): string | undefined {
  if (!raw || !raw.startsWith("j:")) return raw;
  try {
    const parsed: unknown = JSON.parse(raw.slice(2));
    return typeof parsed === "string" ? parsed : raw;
  } catch {
    return raw;
  }
}

export interface UtmTrackingInput {
  /** Raw query string of the current request, no leading '?'. */
  queryString: string;
  /** Referer/referrer header value of the current request, if any. */
  referrerUrl: string | undefined;
  getCookie: (name: string) => string | undefined;
  /** Injectable for tests; defaults to `new Date()`. */
  now?: Date;
}

export interface UtmTrackingResult {
  set: Array<{ name: string; value: string | Date; omitSecurityFlags?: boolean }>;
  clear: string[];
}

function formatDateTime(d: Date): string {
  return `${d.getMonth() + 1}-${d.getDate()}-${d.getFullYear()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}`;
}

function referrerHostname(referrerUrl: string | undefined): string | null {
  if (!referrerUrl) return null;
  try {
    return new URL(referrerUrl).hostname || null;
  } catch {
    return null;
  }
}

function isUtmDateTimeExpired(rawValue: string | undefined, cutoff: Date): boolean {
  if (!rawValue) return false; // matches `new Date(undefined)` => Invalid Date => comparison is always false
  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed < cutoff;
}

export function computeUtmTrackingActions(input: UtmTrackingInput): UtmTrackingResult {
  const now = input.now ?? new Date();
  const currentDateTime = formatDateTime(now);
  const utmExpiryCutoff = new Date(now.getTime() - UTM_EXPIRY_WINDOW_MS);

  const set: Array<{ name: string; value: string | Date; omitSecurityFlags?: boolean }> = [];
  const clear: string[] = [];
  const setCookie = (name: string, value: string | Date) => set.push({ name, value });
  const setCookieOmitSecurityFlags = (name: string, value: string) =>
    set.push({ name, value, omitSecurityFlags: true });
  const clearUtmCookies = () => clear.push(...UTM_COOKIE_CLEAR_SET);

  // Ported verbatim from the original — purpose undocumented there too.
  setCookie(UTM_COOKIE_KEYS.LEGACY, "false");

  const firstTransaction = input.getCookie(UTM_COOKIE_KEYS.TRANSACTION_TIME);
  const activityTime = input.getCookie(UTM_COOKIE_KEYS.ACTIVITY_TIME);
  const loginAttempt = input.getCookie(UTM_COOKIE_KEYS.LOGIN_ATTEMPT);
  const referral = referrerHostname(input.referrerUrl);

  if (referral && referral.includes("industrybuying")) {
    if (firstTransaction) {
      if (!activityTime) {
        clearUtmCookies();
        setCookieOmitSecurityFlags(UTM_COOKIE_KEYS.REFERRAL, "");
        setCookie(UTM_COOKIE_KEYS.DIRECT_DATE_TIME, now);
      }
    } else if (
      isUtmDateTimeExpired(input.getCookie(UTM_COOKIE_KEYS.UTM_DATE_TIME), utmExpiryCutoff)
    ) {
      clearUtmCookies();
      setCookie(UTM_COOKIE_KEYS.REFERRAL, "");
      setCookie(UTM_COOKIE_KEYS.DIRECT_DATE_TIME, now);
    }
    setCookie(UTM_COOKIE_KEYS.ACTIVITY_TIME, currentDateTime);
    return { set, clear };
  }

  if (loginAttempt) {
    clear.push(UTM_COOKIE_KEYS.LOGIN_ATTEMPT);
    setCookie(UTM_COOKIE_KEYS.ACTIVITY_TIME, currentDateTime);
    return { set, clear };
  }

  const queryString = input.queryString ?? "";
  if (queryString.indexOf("utm_source") > -1) {
    clearUtmCookies();
    clear.push(UTM_COOKIE_KEYS.DIRECT_DATE_TIME);

    setCookie(UTM_COOKIE_KEYS.REFERRAL, "UTM");
    setCookie(UTM_COOKIE_KEYS.UTM_DATE_TIME, now);

    for (const pair of queryString.split("&")) {
      const [name, value] = pair.split("=");
      if (name === "utm_source" && value !== undefined) setCookie(UTM_COOKIE_KEYS.UTM_SOURCE, value);
      if (name === "utm_campaign" && value !== undefined) setCookie(UTM_COOKIE_KEYS.UTM_CAMPAIGN, value);
      if (name === "utm_medium" && value !== undefined) setCookie(UTM_COOKIE_KEYS.UTM_MEDIUM, value);
      if (name === "admitad_uid" && value !== undefined) setCookie(UTM_COOKIE_KEYS.ADMITAD_UID, value);
      if (name === "gclid" && value !== undefined) setCookie(UTM_COOKIE_KEYS.GCLID, value);
    }

    if (queryString.indexOf("utm_campaign") < 0) setCookie(UTM_COOKIE_KEYS.UTM_CAMPAIGN, "");
    if (queryString.indexOf("utm_medium") < 0) setCookie(UTM_COOKIE_KEYS.UTM_MEDIUM, "");
    if (queryString.indexOf("gclid") < 0) setCookie(UTM_COOKIE_KEYS.GCLID, "");
  } else if (!input.referrerUrl) {
    if (firstTransaction) {
      if (!activityTime) {
        clearUtmCookies();
        setCookie(UTM_COOKIE_KEYS.REFERRAL, "");
        setCookie(UTM_COOKIE_KEYS.DIRECT_DATE_TIME, now);
      }
    } else if (
      isUtmDateTimeExpired(input.getCookie(UTM_COOKIE_KEYS.UTM_DATE_TIME), utmExpiryCutoff)
    ) {
      clearUtmCookies();
      setCookie(UTM_COOKIE_KEYS.REFERRAL, "");
      setCookie(UTM_COOKIE_KEYS.DIRECT_DATE_TIME, now);
    }
  } else {
    clearUtmCookies();
    clear.push(UTM_COOKIE_KEYS.DIRECT_DATE_TIME);
    setCookie(UTM_COOKIE_KEYS.REFERRAL, input.referrerUrl);
    setCookie(UTM_COOKIE_KEYS.UTM_DATE_TIME, now);
  }

  setCookie(UTM_COOKIE_KEYS.ACTIVITY_TIME, currentDateTime);
  return { set, clear };
}

function readBrowserCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] !== undefined ? decodeURIComponent(match[1]) : undefined;
}

export function applyUtmTrackingInBrowser(input: {
  queryString: string;
  referrerUrl: string | undefined;
}): void {
  const { set, clear } = computeUtmTrackingActions({
    queryString: input.queryString,
    referrerUrl: input.referrerUrl,
    getCookie: readBrowserCookie,
  });

  const domain = window.location.hostname === "localhost" ? undefined : ".industrybuying.com";
  const secure = window.location.protocol === "https:";

  for (const name of clear) {
    document.cookie = [
      `${name}=`,
      "path=/",
      "max-age=0",
      domain ? `domain=${domain}` : "",
    ]
      .filter(Boolean)
      .join("; ");
  }

  for (const { name, value } of set) {
    document.cookie = [
      `${name}=${encodeURIComponent(toExpressCookieValue(value))}`,
      "path=/",
      `max-age=${UTM_COOKIE_MAX_AGE_MS / 1000}`,
      domain ? `domain=${domain}` : "",
      secure ? "Secure" : "",
    ]
      .filter(Boolean)
      .join("; ");
  }
}
